import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Icon from '../../components/AppIcon';
import ProgressChart from '../progress-analytics/components/ProgressChart';
import FeedbackInsightsPanel from './components/FeedbackInsightsPanel';



const FeedbackAnalyticsDashboard = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isAuthenticated, loading: authLoading } = useAuth();
  const [feedbackData, setFeedbackData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    loadFeedbackData();
  }, [currentTenant, dateRange, filterType]);

  const loadFeedbackData = async () => {
    if (!currentTenant?.id) return;

    setLoading(true);
    try {
      const daysAgo = new Date();
      daysAgo?.setDate(daysAgo?.getDate() - parseInt(dateRange));

      let query = supabase
        ?.from('workout_feedback')
        ?.select(`
          *,
          workout_sessions(workout_name, total_duration_seconds, actual_calories),
          user_profiles(full_name, email)
        `)
        ?.eq('tenant_id', currentTenant?.id)
        ?.gte('created_at', daysAgo?.toISOString())
        ?.order('created_at', { ascending: true });

      const { data, error } = await query;

      if (error) throw error;
      setFeedbackData(data || []);
    } catch (error) {
      console.error('Error loading feedback data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateKPIs = () => {
    if (feedbackData?.length === 0) {
      return {
        avgSatisfaction: 0,
        totalFeedback: 0,
        responseRate: 0,
        improvementTrend: 0
      };
    }

    const avgSatisfaction = feedbackData?.reduce((sum, f) => sum + (f?.rating || 0), 0) / feedbackData?.length;
    const totalFeedback = feedbackData?.length;
    
    // Calculate response rate (mock calculation - in real app, compare with total workouts)
    const responseRate = 75; // Mock value

    // Calculate improvement trend (compare first half vs second half)
    const midpoint = Math.floor(feedbackData?.length / 2);
    const firstHalfAvg = feedbackData?.slice(0, midpoint)?.reduce((sum, f) => sum + (f?.rating || 0), 0) / midpoint || 0;
    const secondHalfAvg = feedbackData?.slice(midpoint)?.reduce((sum, f) => sum + (f?.rating || 0), 0) / (feedbackData?.length - midpoint) || 0;
    const improvementTrend = ((secondHalfAvg - firstHalfAvg) / firstHalfAvg * 100) || 0;

    return {
      avgSatisfaction: avgSatisfaction?.toFixed(1),
      totalFeedback,
      responseRate,
      improvementTrend: improvementTrend?.toFixed(1)
    };
  };

  const getSatisfactionTrendData = () => {
    if (feedbackData?.length === 0) return [];

    const groupedByDate = {};
    feedbackData?.forEach(feedback => {
      const date = new Date(feedback?.created_at)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      if (!groupedByDate?.[date]) {
        groupedByDate[date] = { date, ratings: [], count: 0 };
      }
      groupedByDate?.[date]?.ratings?.push(feedback?.rating);
      groupedByDate[date].count++;
    });

    return Object?.values(groupedByDate)?.map(group => ({
      name: group?.date,
      satisfaction: (group?.ratings?.reduce((a, b) => a + b, 0) / group?.ratings?.length)?.toFixed(1),
      volume: group?.count
    }));
  };

  const getRatingDistributionData = () => {
    const distribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    feedbackData?.forEach(f => {
      if (f?.rating) distribution[f?.rating]++;
    });
    return Object?.entries(distribution)?.map(([rating, count]) => ({
      name: `${rating}★`,
      count,
      percentage: feedbackData?.length > 0 ? ((count / feedbackData?.length) * 100)?.toFixed(0) : 0
    }));
  };

  const getDifficultyBreakdown = () => {
    const breakdown = { too_easy: 0, just_right: 0, too_hard: 0 };
    feedbackData?.forEach(f => {
      if (f?.difficulty_level) breakdown[f?.difficulty_level]++;
    });
    return Object?.entries(breakdown)?.map(([level, count]) => ({
      name: level?.replace('_', ' ')?.replace(/\b\w/g, l => l?.toUpperCase()),
      count
    }));
  };

  const kpis = calculateKPIs();
  const satisfactionTrendData = getSatisfactionTrendData();
  const ratingDistributionData = getRatingDistributionData();
  const difficultyBreakdownData = getDifficultyBreakdown();

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="max-w-7xl mx-auto px-6 py-8 pb-24">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Feedback Analytics Dashboard</h1>
              <p className="text-muted-foreground">Transform member feedback into actionable business intelligence</p>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e?.target?.value)}
                className="px-4 py-2 bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
              </select>
              <button
                onClick={loadFeedbackData}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
              >
                <Icon name="RefreshCw" size={18} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Icon name="Loader" size={32} className="text-primary animate-spin mx-auto mb-2" />
              <p className="text-muted-foreground">Loading analytics...</p>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-card border border-border rounded-lg p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 rounded-lg bg-warning/20 flex items-center justify-center">
                    <Icon name="Star" size={24} className="text-warning" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Avg Satisfaction</p>
                    <p className="text-3xl font-bold text-foreground">{kpis?.avgSatisfaction}</p>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center">
                    <Icon name="MessageSquare" size={24} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Feedback</p>
                    <p className="text-3xl font-bold text-foreground">{kpis?.totalFeedback}</p>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 rounded-lg bg-success/20 flex items-center justify-center">
                    <Icon name="TrendingUp" size={24} className="text-success" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Response Rate</p>
                    <p className="text-3xl font-bold text-foreground">{kpis?.responseRate}%</p>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 rounded-lg bg-accent/20 flex items-center justify-center">
                    <Icon name="Activity" size={24} className="text-accent" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Improvement</p>
                    <p className="text-3xl font-bold text-foreground">
                      {kpis?.improvementTrend > 0 ? '+' : ''}{kpis?.improvementTrend}%
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Charts */}
            {feedbackData?.length > 0 ? (
              <>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <ProgressChart
                    title="Satisfaction Trends"
                    data={satisfactionTrendData}
                    type="line"
                    dataKeys={[
                      { dataKey: 'satisfaction', name: 'Avg Rating' },
                      { dataKey: 'volume', name: 'Feedback Count' }
                    ]}
                    colors={['var(--color-primary)', 'var(--color-accent)']}
                  />
                  <ProgressChart
                    title="Rating Distribution"
                    data={ratingDistributionData}
                    type="bar"
                    dataKeys={[{ dataKey: 'count', name: 'Count' }]}
                    colors={['var(--color-warning)']}
                  />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <ProgressChart
                    title="Difficulty Breakdown"
                    data={difficultyBreakdownData}
                    type="bar"
                    dataKeys={[{ dataKey: 'count', name: 'Responses' }]}
                    colors={['var(--color-success)']}
                  />
                  <FeedbackInsightsPanel feedbackData={feedbackData} />
                </div>
              </>
            ) : (
              <div className="bg-card border border-border rounded-lg p-12 text-center">
                <Icon name="BarChart3" size={48} className="text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">No Feedback Data Available</h3>
                <p className="text-muted-foreground">Member feedback will appear here once workouts are completed</p>
              </div>
            )}
          </div>
        )}
      </main>

      <MobileBottomNav />
    </div>
  );
};

export default FeedbackAnalyticsDashboard;